import {
  ColumnData,
  SlideAction,
  SlideLayout,
  type SlideLayoutLocationState,
  useSlideLayout,
  useSlideLayoutSafe,
  useSlidePanel,
  ViewMode,
} from '@gripp/shared-ui';
import { useCurrentWorkspace } from 'libs/shared-logic/src/auth/hooks/useCurrentWorkspace';
import { useCallback, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import { NewIssueBody } from '../../issues';
import { HeaderResetButton } from '../../shared/components/headerResetButton';
import ActivityEditLabels from './activity-edit-label';
import { ActivityFilters } from './activityFilters';
import { ActivityFoldOneColumn } from './activityFoldOneColumn';

const ACTIVITY_NEW_PANEL_SOURCE =
  'gripp-workspace/apps/gripp-web/src/activity/components/activities-new.tsx';
const ACTIVITY_ROUTES_SOURCE =
  'gripp-workspace/apps/gripp-web/src/activity/routes.tsx';
const NEW_ISSUE_COMPONENT_SOURCE =
  'gripp-workspace/apps/gripp-web/src/issues/components/newIssueModal.tsx';
const ACTIVITY_NEW_ROUTE_PATTERN = '/activities/new';

export const ActivityNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const slideLayout = useSlideLayoutSafe();
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.DUAL
      ? locationState.slideLayoutInitialTransition
      : undefined;
  const [selectedLabels, setSelectedLabels] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [isLabelsPanelOpen, setIsLabelsPanelOpen] = useState(false);
  const currentWorkspace = useCurrentWorkspace();
  const workspaceId =
    typeof currentWorkspace === 'string'
      ? currentWorkspace
      : currentWorkspace?.id;
  const resetLabelsRef = useRef<(() => void) | undefined>(undefined);

  const onIssueSelected = useCallback(
    (issueId: string) => {
      navigate(`/activities/${issueId}`);
    },
    [navigate]
  );

  // NewIssueBody calls onComplete after onIssueCreated; keep the detail route.
  const keepCreatedActivityRoute = useCallback(() => undefined, []);

  const handleFilterChange = useCallback(() => {
    navigate('/activities/filters', { state: { skipAutoSelect: true } });
  }, [navigate]);

  const leftColumn = useMemo<ColumnData>(
    () => ({
      title: 'activity.menu.title',
      mainComponent: (
        <ActivityLeftColumn
          onIssueSelected={onIssueSelected}
          showAssetName={true}
          hideFilterBar={true}
        />
      ),
      headerComponent: <ActivityFilters onFilterChange={handleFilterChange} />,
      disableScroll: true,
    }),
    [handleFilterChange, onIssueSelected]
  );

  const rightColumn = useMemo<ColumnData>(
    () => ({
      title: 'issue.new.addIt',
      mainComponent: (
        <NewIssueBody
          workspaceId={workspaceId}
          isOpened={true}
          modalType="sheet"
          onComplete={keepCreatedActivityRoute}
          onCancel={() => navigate('/activities')}
          onIssueCreated={(issueId) => navigate(`/activities/${issueId}`)}
          selectedLabels={selectedLabels}
          onLabelsChange={setSelectedLabels}
          onSelectorEdit={() => setIsLabelsPanelOpen(true)}
        />
      ),
      showBackButton: true,
      backButtonTransition: SlideAction.SINGLE,
      onBackButtonPress: () => {
        navigate('/activities', { state: { skipAutoSelect: true } });
      },
      disableScroll: true,
      debug: {
        id: 'activity-new:right-new-issue',
        titleKey: 'issue.new.addIt',
        component: 'NewIssueBody',
        componentSource: NEW_ISSUE_COMPONENT_SOURCE,
        columnSource: ACTIVITY_NEW_PANEL_SOURCE,
        ownerComponent: 'ActivityNew',
        ownerSource: ACTIVITY_NEW_PANEL_SOURCE,
        routePanelKey: 'activity-new',
        routePattern: ACTIVITY_NEW_ROUTE_PATTERN,
        routeSource: ACTIVITY_ROUTES_SOURCE,
        source: {
          owner: {
            file: ACTIVITY_NEW_PANEL_SOURCE,
            symbol: 'ActivityNew',
          },
          column: {
            file: ACTIVITY_NEW_PANEL_SOURCE,
            symbol: 'rightColumn',
          },
          component: {
            file: NEW_ISSUE_COMPONENT_SOURCE,
            symbol: 'NewIssueBody',
          },
          route: {
            file: ACTIVITY_ROUTES_SOURCE,
            symbol: 'activityRoutes',
          },
        },
      },
    }),
    [keepCreatedActivityRoute, navigate, selectedLabels, workspaceId]
  );

  const thirdColumn = useMemo<ColumnData | null>(
    () =>
      isLabelsPanelOpen
        ? {
            title: 'asset.groups',
            mainComponent: (
              <ActivityEditLabels
                selectedLabels={selectedLabels ?? []}
                onLabelsChange={setSelectedLabels}
                resetRef={resetLabelsRef}
              />
            ),
            headerComponent: (
              <View>
                <HeaderResetButton onReset={() => resetLabelsRef.current?.()} />
              </View>
            ),
            showBackButton: true,
            disableScroll: true,
            onBackButtonPress: () => setIsLabelsPanelOpen(false),
          }
        : null,
    [isLabelsPanelOpen, selectedLabels]
  );

  useSlidePanel('right', rightColumn, [rightColumn]);
  useSlidePanel('third', thirdColumn, [thirdColumn]);

  if (slideLayout) {
    return null;
  }

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumn ?? undefined}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};

const ActivityLeftColumn = ({
  onIssueSelected,
  showAssetName,
  selectedIssue,
  hideFilterBar,
}: {
  onIssueSelected: (issueId: string) => void;
  showAssetName: boolean;
  selectedIssue?: string;
  hideFilterBar: boolean;
}) => {
  const { onSlideChange, currentViewMode } = useSlideLayout();

  const handleIssueSelected = useCallback(
    (issueId: string) => {
      if (currentViewMode !== ViewMode.DUAL) {
        onSlideChange(SlideAction.DUAL);
      }
      onIssueSelected(issueId);
    },
    [onSlideChange, currentViewMode, onIssueSelected]
  );

  return (
    <ActivityFoldOneColumn
      onIssueSelected={handleIssueSelected}
      showAssetName={showAssetName}
      selectedIssue={selectedIssue}
      hideFilterBar={hideFilterBar}
    />
  );
};
